// Cosmetic sea: one large plane at the water surface, shaded entirely in the fragment shader.
// Human-facing only. The simulation treats the seabed as ordinary ground (the quadruped can
// wade or walk out), so none of this feeds back.
//
// What the shader does, per pixel:
//   surface   the plane sits a little above the mean level; the shader marches the view ray down through a thin slab around the mean level until it
//             meets the water surface (the sea's waves plus the beach's bore and swash, below) or the ground, whichever comes first. So a crest hides
//             what is behind it, and the swash stops exactly where the sand rises out of it. Beyond about 90 m the surface is flat and nothing is marched.
//   depth     the water's depth under that point: its surface height minus the ground height, sampled from the same heightfield texture the grass
//             uses (clamped at the edge, so beyond the world the sea is as deep as its rim).
//   waves     the sea's wave spectrum, evolved on the GPU by an FFT (waterSpectrum.ts): three repeating tiles of slope maps, 560 m, 87 m and 14 m
//             across, with mip levels. The shader sums the three. A mip level holds the mean slope of the waves it can resolve and the mean
//             square of the slope, so the slope it cannot resolve (distant, small waves) is known per pixel as a variance. That variance
//             widens the sky reflection and the sun's glitter and the far sea goes smooth and glossy, not grainy. (The sines this replaced
//             had no such filter and striped at grazing angles.)
//   wind      the spectrum is built from a wind speed, a direction, a fetch and the sea's depth (a JONSWAP sea with the TMA shallow-water
//             correction), set through setSeaState(); the local gust field (wind.ts) roughens the short waves where a gust crosses
//             the water, so cat's paws travel over the sea with the gusts that bend the grass.
//   shore     in the last metres of depth the waves follow their own model. waterShore.ts solves the phase of a wave train over the terrain's
//             actual bottom (refraction: the crests bend to run parallel to the beach and bunch up as the water shallows). From that phase
//             the shader draws crests that grow with the shoaling water (Green's law with the exact dispersion relation), steepen into a
//             forward-leaning bore where a wave is as high as 0.78 of the depth, leave foam that thins to lace as the time since the wave passed
//             grows, and send a thin tongue of water up the sand and back (a ballistic run-up with a foam edge). Each wave has its own height,
//             and the crests arrive at the beach a little out of step along it. A shore facing the waves gets all of them, a lee shore a third.
//   foam      white caps where the waves fold (the Jacobian of the FFT's horizontal displacement, kept per tile with a fade), and the bore's foam.
//   colour    shallow turquoise to deep blue by depth, with opacity rising from 0 at the waterline, so the sand shows through the shallows;
//             wave crests turn green where the sun shines through them toward the eye.
//   sky       what the sea reflects, and the fog it melts into, is the sky itself: the sky dome is drawn into a
//             small cube map around the eye (Sky.updateProbe) and the shader looks it up along the reflected ray
//             (at a blur set by the unresolved slope) and along the view ray at the horizon.
//   light     a MeshStandardMaterial, so the sun's glint, ambient light, fog and tone mapping all
//             come from the scene like everything else. Sky reflection (Fresnel) is mixed in
//             AFTER tone mapping, from the display-range sky colour the renderer already
//             computes, so the far water melts into the horizon fog.
//   glitter   the sun's and the moon's reflection off the wave facets, drawn after tone mapping. A
//             Beckmann lobe whose width is the slope variance the pixel cannot resolve (plus a floor for the sun's disc), so near water
//             sparkles and far water gives a broad shimmering path. Each light's strength arrives as a
//             vector K in the sky curve's units (SKY_EXPOSURE * display gain * beam irradiance), so
//             the sun saturates to white and a full moon gives a soft silver path. The scene's
//             directional light only exists for the sun, so the moon's path is made here.
//   below     seen from underneath (or with the eye under the surface) the plane becomes the
//             underside of the sea: a dark teal ceiling with a bright Snell's window overhead.
//             The renderer tints the scene fog to match while the eye is under.
//
// The surface height (sea level plus tide) is set by the renderer every frame. Sim (x, y, z)
// maps to three (x, z, -y), so the sea surface is a horizontal plane at three y.
//
// Call order per frame: update() (placement, light, wind), then simulate(renderer, t, dt) (the wave FFT; once per frame, outside the main render).
// Without a float-texture-capable context (WebGL2 without EXT_color_buffer_float) the sea falls back to a few filtered sine swells.
import * as THREE from 'three';
import { GROUND_GLSL, type GroundUniforms, type ShoreWindow } from './groundClip';
import { WIND_GLSL } from './wind';
import { origin } from './origin';
import { OceanFFT, DEFAULT_SEA, CASCADE_L, seaStats, SeaStatsJob, peakOmega, swellMoved, type SeaState, type SeaStats, type SwellTrain } from './waterSpectrum.ts';
import { buildShoreField, hunt, OMEGA_REF, type ShoreField } from './waterShore';
import { air, AIR_GLSL } from './air';
import { curvedDisc } from './planet';

const STATS_SLICE_MS = 1.5;      // CPU time the wave-statistics job may take in a frame
const STATS_MIN_GAP_S = 2.0;      // fewest seconds between two jobs
const SWELL_TAU_S = 60;            // seconds a swell train takes to follow the server's (the storms change over hours; this hides the steps and a train's arrival)
const wrapAngle = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));
const STATS_MOVE = 0.01;          // the sea state must have moved this share (speed, fetch, depth) from the last result's before a new job starts

const PLANE_M = 1800;            // metres on a side at rest; setReach() grows it to the eye's reach (the camera's far plane), so it has no edge in view
const RAIN_CELL_M = 0.32;       // the rain rings' grid (m): O is a whole number of these (origin.ts)
const SNAP_M = 16;               // the plane follows the eye in steps of this (waves use world coordinates, so nothing swims)
const G = 9.81;

// Look tuning. Colours are linear RGB.
export const SHALLOW = [0.030, 0.420, 0.400] as const;
export const DEEP    = [0.004, 0.060, 0.140] as const;
const SCATTER = [0.020, 0.300, 0.240] as const;   // what sunlit crests glow with when the light passes through them
export const ABSORB_COLOUR = 0.55;      // 1/metres: how fast the colour goes from shallow to deep with depth
export const ABSORB_ALPHA  = 1.15;      // 1/metres: how fast the water goes opaque with depth
export const CUBE_RES = 128;            // face size of the sky probe (sky.ts PROBE_SIZE): sets which mip level matches a reflection lobe
export const FOG_LOD  = 1.0;            // mip level of the sky probe the horizon colour is read from
const GLITTER_GAIN  = 0.05;      // scales the reflection lobe (its peak is about 1 / (pi m^2) before this)
const MOON_GLITTER_BOOST = 8.0;  // the moon's beam is a million times dimmer than the sun's; a reflection of it is
                                 // judged against a dark sea, so it is lifted. 1 = the sky curve's own value.
export const GLITTER_FLOOR = 0.0004;    // m^2 floor of the glitter lobe: the sun's disc and the sea's finest ripples are not infinitely sharp
export const PROBE_FLOOR = 0.0002;      // slope variance floor of the sky reflection's blur
export const STAR_REFL_GAIN = 1.0;      // strength of the stars and the Milky Way in the reflection (1 = as the sky itself shows them; starProbe.ts)
const MARCH_NEAR_M = 55;         // the surface's height fades out between these distances from the eye ...
const MARCH_FAR_M  = 95;         // ... and the ray is no longer marched beyond
const SHORE_EDGE_CELLS = 24;           // cells of the shore window's edge over which the beach waves fade out
const SURF_D0 = 1.8, SURF_D1 = 3.2;   // m of still-water depth between which the open sea's waves hand over to the beach waves
const BREAK_GAMMA = 0.78;        // a wave breaks where it is this high compared with the depth
// Cost of the ray march (the sea's dominant pixel cost). The step count falls with distance; the bisection that polishes the hit needs only as many
// halvings as the pixel's footprint can show, except in shallow water (see the march). Capping the steps by the ray's path length was tried and
// moved the waterline against a steep bank (the coarser steps find a different first crossing), so it is not done.
const MARCH_STEP_D0 = 14;        // m from the eye: the full step count holds out to here ...
const MARCH_STEP_D1 = 50;        // ... and has fallen to MARCH_FAR_FRAC of itself by here (far waves are mip-filtered smooth: the steps would find nothing)
const MARCH_FAR_FRAC = 0.4;      // share of the quality level's steps kept at MARCH_STEP_D1 and beyond
const MARCH_MIN_STEPS = 4;       // never fewer coarse steps than this
const SHALLOW_FULL_M = 1.0;      // m of still-water depth under the hit within which the bisection runs at full precision
const DEEP_SKIP_M = SURF_D1 + 0.05;  // m of still-water depth beyond which the height needs neither the beach waves nor the swash (and, being over 1.8 m,
                                     // deeper than the slab can be, the ground cannot show through). Tested against the 3x3 maximum of the ground (uFieldMax),
                                     // which bounds the bilinear ground exactly, so no margin is needed even on a cliff.

const WATER_COMMON = /* glsl */`
${GROUND_GLSL}
uniform float uSeaY;
uniform float uWT;
uniform vec2  uWaveDir;        // the direction the waves travel toward (three x, z)
uniform vec3  uShallow;
uniform vec3  uDeep;
uniform vec3  uScatter;
uniform vec3  uSky;            // display-range linear sky colour (the horizon average; used until the probe has drawn, and underwater)
uniform samplerCube uSkyCube;  // the sky dome as seen from the eye, display range
uniform float uSkyCubeOn;      // 1 once the probe has been drawn
uniform samplerCube uStarCube; // the stars and the Milky Way as seen from the eye, display range (starProbe.ts)
uniform float uStarCubeOn;     // 1 while that cube holds a night sky
uniform float uFogDensity;     // exp2 fog density of the scene (the underwater look; none in air)
${AIR_GLSL}
uniform vec3  uUnderCol;
uniform float uUnder;          // 1 when the eye is under the surface
uniform float uNearFade;       // 1: the sea is drawn whole; less: it is that opaque (the near field's fade-out with height, bodies.ts nearFade: the planet the far shell draws under it shows through); see WATER_POST
uniform vec3  uSunDir;         // unit vectors toward the sun and the moon, three axes (y up)
uniform vec3  uSunK;           // their strength per channel, in the sky curve's units (0 when below the horizon)
uniform vec3  uMoonDir;
uniform vec3  uMoonK;
uniform sampler2D uSA0, uSA1, uSA2;   // wave tiles: slope x, slope z, height, Jacobian
uniform sampler2D uSB0, uSB1, uSB2;   // ... and slope x^2, slope z^2, slope x*z, foam
uniform vec3  uTileL;          // tile side lengths, m
uniform float uTiles;          // tiles in use (1 to 3)
uniform float uFFT;            // 1 when the FFT waves are running, 0 for the fallback sines
uniform float uHs;             // significant wave height, m
uniform float uGustOn;         // 1 when the wind field is bound
uniform sampler2D uShore;      // beach wave phase: psi, d psi/dx, d psi/dz, ground slope
uniform vec4  uShoreInfo;      // nx, ny, width, height
uniform vec2  uShoreOrg;       // the field's sample (0, 0), in the world (three x, z)
uniform float uShoreOn;        // 1 when there is a shore field
uniform float uPhi;            // wave phase of the beach waves, rad (the integral of the peak frequency over time)
uniform float uOmega;          // peak angular frequency, rad/s
uniform float uOmegaRef;       // the frequency the phase field was solved for
uniform float uHinc;           // height of the long waves that reach the beach, m
uniform float uCgOff;          // their group speed in the offshore depth, m/s
uniform float uMarch;          // ray-march steps (0 = flat plane)
uniform float uHtop;           // the slab the surface lives in: this far above and
uniform float uHlow;           // ... below the mean level
uniform float uSwashT;         // seconds of swash per wave (the wave period)
uniform vec4  uRain[6];        // raining discs near the eye: x, z (three axes), radius, rain rate mm/h at the centre
uniform float uRainN;          // how many are in use
uniform vec2  uOriginXZ;            // the floating origin O (x, z), m (origin.ts): every position here is counted from it; the lookups that need the whole position add it back
uniform vec3  uEyeRel;         // the eye relative to O (x, y, z)
uniform vec2  uTO0, uTO1, uTO2; // O modulo each tile's side: what takes a position counted from O to the same place on the tile
uniform vec2  uNC5, uNC6, uNC17, uNCR;   // O in hash cells at the noises' scales (5, 6, 17 per metre; the rain's grid): whole numbers
varying vec3  vWPos;

float wFoam;
vec3  wN;
float wSig2;                   // slope variance (per axis) the pixel's waves have that the normal does not carry
float wEta;                    // surface height above the mean level, m
float wCapFoam;
float wRing;                   // brightness of rain rings, 0 to 1

${WIND_GLSL}

vec3 uSkyCube_on(vec3 d, float lod) { return mix(uSky, textureLod(uSkyCube, d, lod).rgb, uSkyCubeOn); }
vec3 uStarCube_on(vec3 d, float lod) { return uStarCubeOn * textureLod(uStarCube, d, lod).rgb; }

// The ground under p (counted from O): the finest ring of the ground clipmap that draws it (groundClip.ts).
float wGround(vec2 xz) { return groundYRel(xz); }
// An upper bound of the ground height at p, from one fetch where wGround takes four (for the question "is it deep here?"). The bilinear ground
// is a mix of the four samples floor(uv) + {0, 1}; the nearest sample is one of them, so all four lie in its 3x3 block, and the maximum texture holds that block's maximum.
float wGroundMax3(vec2 xz) { return groundMaxRel(xz); }
// the beach's wave phase field at p: (psi, d psi/dx, d psi/dz, ground slope), bilinear
vec4 wShoreAt(vec2 xz) {
  xz += uOriginXZ - uShoreOrg;
  vec2 uv = vec2(xz.x / uShoreInfo.z * (uShoreInfo.x - 1.0), -xz.y / uShoreInfo.w * (uShoreInfo.y - 1.0));
  uv = clamp(uv, vec2(0.0), uShoreInfo.xy - 1.0001);
  ivec2 i = ivec2(floor(uv));
  vec2 f = uv - vec2(i);
  vec4 a = texelFetch(uShore, i, 0);
  vec4 b = texelFetch(uShore, i + ivec2(1, 0), 0);
  vec4 c = texelFetch(uShore, i + ivec2(0, 1), 0);
  vec4 d = texelFetch(uShore, i + ivec2(1, 1), 0);
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
// How much of the beach waves' weight the shore window gives at p: 1 inside it, falling to 0 over its outer ${SHORE_EDGE_CELLS} cells and 0 beyond it (its phase is only right where it was solved)
float wShoreIn(vec2 xz) {
  xz += uOriginXZ - uShoreOrg;
  vec2 uv = vec2(xz.x / uShoreInfo.z * (uShoreInfo.x - 1.0), -xz.y / uShoreInfo.w * (uShoreInfo.y - 1.0));
  vec2 e = min(uv, uShoreInfo.xy - 1.0 - uv);
  return smoothstep(0.0, ${SHORE_EDGE_CELLS.toFixed(1)}, min(e.x, e.y));
}
float wHash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float wNoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(wHash(i), wHash(i + vec2(1.0, 0.0)), f.x),
             mix(wHash(i + vec2(0.0, 1.0)), wHash(i + vec2(1.0, 1.0)), f.x), f.y);
}

// The same noise with the cell indices counted from the world origin though p is counted from O: off is O in cells (whole numbers, origin.ts), and an index is kept in [-2^21, 2^21) so that it
// stays exact in a float; off = 0 is wNoise.
vec2 wCell(vec2 i) { return i - 4194304.0 * floor((i + 2097152.0) / 4194304.0); }
float wNoiseO(vec2 p, vec2 off) {
  vec2 i = floor(p) + off, f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(wHash(wCell(i)), wHash(wCell(i + vec2(1.0, 0.0))), f.x),
             mix(wHash(wCell(i + vec2(0.0, 1.0))), wHash(wCell(i + vec2(1.0, 1.0))), f.x), f.y);
}

// One tile's contribution at p: its mean slope (what the mip level resolves) and the slope variance it has lost, its height and its foam.
void wTile(sampler2D A, sampler2D B, float L, vec2 o, float g, vec2 p, vec2 gx, vec2 gy, float useEta, inout vec2 S, inout vec3 V2, inout float eta, inout float foam) {
  vec2 uv = (p + o) / L;
  vec4 a = textureGrad(A, uv, gx / L, gy / L), b = textureGrad(B, uv, gx / L, gy / L);
  S += a.xy * g;
  V2 += vec3(max(b.x - a.x * a.x, 0.0), max(b.y - a.y * a.y, 0.0), b.z - a.x * a.y) * (g * g);
  eta += a.z * g * useEta;
  foam = max(foam, b.w);
}
// the same for the height alone (inside the ray march). One trilinear fetch at the mip level of the pixel's widest footprint: the march only needs
// a height smooth at the scale the pixel can show, and the anisotropic filter textureGrad would run on the grazing, very elongated footprint
// (the tiles carry anisotropy 8) costs up to eight times as many taps, on every one of the march's evaluations. The shading below keeps textureGrad.
float wTileEta(sampler2D A, float L, vec2 o, float g, vec2 p, vec2 gx, vec2 gy) {
  float lod = max(0.0, log2(max(length(gx), length(gy)) * float(textureSize(A, 0).x) / L));
  return textureLod(A, (p + o) / L, lod).z * g;
}

// Fallback waves (no float render targets): five sine swells from long to fine, each faded by the pixel footprint fp (m) so that none
// aliases, the slope they lose added to the variance. Returns the gradient of the surface height at p (metres per metre).
vec2 wFallback(vec2 p, vec2 d0, float t, float fp, inout float var) {
  vec2 d1 = vec2(d0.x * 0.8192 - d0.y * 0.5736, d0.x * 0.5736 + d0.y * 0.8192);
  vec2 d2 = vec2(d0.x * 0.6428 + d0.y * 0.7660, -d0.x * 0.7660 + d0.y * 0.6428);
  vec2 d3 = vec2(-d0.y, d0.x);
  vec2 g = vec2(0.0);
  vec2 dirs[5] = vec2[5](d0, d1, d2, d3, d0);
  float ks[5]  = float[5](0.55, 1.30, 2.90, 6.10, 11.0);
  float as[5]  = float[5](0.050, 0.022, 0.012, 0.0045, 0.0025);
  float ws[5]  = float[5](1.05, 1.70, 2.60, 3.40, -4.10);
  for (int i = 0; i < 5; i++) {
    float ph = dot(dirs[i], p) * ks[i] + t * ws[i] + (i == 4 ? 1.3 : 0.0);
    float kfp = ks[i] * fp / 6.2831853;                     // pixel footprint in wavelengths
    float res = 1.0 - smoothstep(0.12, 0.35, kfp);          // resolved share
    float sa = as[i] * ks[i];
    g += dirs[i] * (sa * cos(ph) * res);
    var += 0.25 * sa * sa * (1.0 - res * res);              // the lost slope variance (per axis, half the energy of a sine)
  }
  return g;
}

// ---- the beach waves ------------------------------------------------------------------------------------------------------------------------
// Wave number of a wave of angular frequency w in depth d (Hunt's approximation of w^2 = g k tanh(k d)), and its group speed.
float wHunt(float w, float d) {
  d = clamp(d, 0.05, 8.0);
  float y = w * w * d / ${G.toFixed(2)};
  float kd2 = y * y + y / (1.0 + y * (0.6522 + y * (0.4622 + y * y * y * (0.0864 + y * 0.0675))));
  return sqrt(kd2) / d;
}
float wCg(float w, float d) {
  d = clamp(d, 0.05, 8.0);
  float k = wHunt(w, d), kd = k * d;
  return 0.5 * w / k * (1.0 + 2.0 * kd / sinh(2.0 * kd));
}
// shoaling: the wave grows as its group speed falls (Green's law)
float wKs(float d) { return sqrt(uCgOff / wCg(uOmega, d)); }
// Rain on the water: drops land on a jittered grid, each starting a ring that spreads and fades; returns the slope the rings add.
// Disc rates are Gaussian in the distance from the disc's centre; the drop density follows the rate.
float wRainRate(vec2 p) {
  float r = 0.0;
  for (int i = 0; i < 6; i++) {
    if (float(i) >= uRainN) break;
    vec2 d = p - uRain[i].xy;
    r += uRain[i].w * exp(-dot(d, d) / max(uRain[i].z * uRain[i].z, 1.0));
  }
  return r;
}
vec2 wRainSlope(vec2 p, float rate, float t) {
  float dens = clamp(rate / 14.0, 0.0, 1.0);
  if (dens < 0.02) return vec2(0.0);
  const float CELL = ${RAIN_CELL_M.toFixed(2)};
  vec2 g = p / CELL, gi = floor(g);
  vec2 S = vec2(0.0);
  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
    vec2 c = gi + vec2(float(i), float(j));
    vec2 ca = wCell(c + uNCR);                                      // the cell's index from the world origin (the hash's input)
    float h0 = wHash(ca + 17.0), h1 = wHash(ca + 41.0), h2 = wHash(ca + 83.0), h3 = wHash(ca + 127.0);
    if (h3 > dens) continue;
    float life = fract(t * (0.8 + 0.5 * h0) + h1);                  // 0 at the drop, 1 when the ring has gone
    vec2 ctr = (c + vec2(0.2 + 0.6 * h1, 0.2 + 0.6 * h2)) * CELL;
    vec2 dp = p - ctr;
    float rho = length(dp);
    float R = life * 0.26, e = rho - R;
    const float W = 0.028, K = 150.0;
    float env = exp(-(e * e) / (W * W)) * (1.0 - life) * (1.0 - life);
    float dh = (K * cos(K * e) - 2.0 * e / (W * W) * sin(K * e)) * env;
    S += 0.0011 * dh * dp / max(rho, 1e-4);
  }
  return S;
}
// the waves arrive a little out of step along the beach, and each has its own height
float wDphi(vec2 p) { p += uOriginXZ; return 2.6 * (wNoise(p * 0.017 + vec2(3.1, 7.7)) - 0.5) + 1.4 * (wNoise(p * 0.055 + vec2(9.2, 1.3)) - 0.5); }
float wWaveF(float iw, vec2 p) {
  float n = smoothstep(0.2, 0.8, wNoise(vec2(iw * 1.713 + 0.37, iw * 0.311) + (p + uOriginXZ) * 0.024));
  return 0.30 + 1.10 * pow(n, 1.5);
}

// Profile of one wave, as a function of the phase u (0 at the crest just behind the front, 1 at the front): a tail that falls away behind the
// crest and a front that rises to the next crest. Smooth swell has a wide front and a flat tail; a breaking wave has a short steep front (the bore).
struct WBore { float Ha, Hn, wn, pa, ma, mn, ba, bn; };
WBore wBoreParams(float iw, vec2 p, float d, float ke) {
  WBore B;
  float Hsat = ${BREAK_GAMMA.toFixed(2)} * d;
  float Hua = uHinc * ke * wWaveF(iw, p), Hun = uHinc * ke * wWaveF(iw + 1.0, p);
  B.Ha = min(Hua, Hsat); B.Hn = min(Hun, Hsat);
  B.ba = smoothstep(0.55, 1.0, Hua / max(Hsat, 1e-3)); B.bn = smoothstep(0.55, 1.0, Hun / max(Hsat, 1e-3));
  float wa = mix(0.42, 0.075, B.ba); B.wn = mix(0.42, 0.075, B.bn);
  B.pa = mix(2.6, 2.0, B.ba);
  float pn = mix(2.6, 2.0, B.bn);
  B.ma = (1.0 - wa) / (B.pa + 1.0) + 0.5 * wa;
  B.mn = (1.0 - B.wn) / (pn + 1.0) + 0.5 * B.wn;
  return B;
}
float wBoreEta(float u, WBore B) {
  float lim = 1.0 - B.wn;
  if (u < lim) return B.Ha * (pow(max(1.0 - u / lim, 0.0), B.pa) - B.ma);
  float x = (u - lim) / B.wn;
  return mix(-B.Ha * B.ma, B.Hn * (1.0 - B.mn), x * x * (3.0 - 2.0 * x));
}

// Height of the swash film above the ground at ground height h above the still level (positive = dry land), and its state. The tongue's front
// reaches the elevation Rz at mid-cycle and runs back (a ballistic run-up); a = 0 as a bore reaches the waterline.
float wSwash(vec2 p, float hAbove, out float edge, out float a) {
  edge = 0.0; a = 0.0;
  if (uShoreOn < 0.5 || hAbove > 0.35 || wShoreIn(p) <= 0.0) return 0.0;
  float cyc = (uPhi + wDphi(p)) / 6.2831853;
  float iw = floor(cyc);
  a = cyc - iw;
  // the beach's own seaward normal from the ground (the waves' exposure there)
  float e = 0.8;
  vec2 gr = vec2(wGround(p + vec2(e, 0.0)) - wGround(p - vec2(e, 0.0)), wGround(p + vec2(0.0, e)) - wGround(p - vec2(0.0, e)));
  vec2 nS = -gr / max(length(gr), 1e-5);
  float expo = mix(0.30, 1.0, smoothstep(-0.4, 0.7, dot(nS, -uWaveDir)));
  float Hs = min(uHinc * expo * wKs(0.7) * wWaveF(iw, p), ${BREAK_GAMMA.toFixed(2)} * 0.7);
  float Rz = 0.30 * Hs + 0.004;
  float zf = Rz * 4.0 * a * (1.0 - a);
  float x = clamp((zf - hAbove) / Rz, 0.0, 1.0);
  edge = (1.0 - smoothstep(0.0, 0.22, x)) * step(0.0, x - 1e-4) * step(a, 0.55);
  return (0.010 + 0.12 * Rz) * sqrt(x);
}

// Surface height (absolute y) of the water, or of the ground where there is none, at p: the sea's waves, the beach's waves and the swash.
// gx, gy: the pixel's footprint (for the mip level); fade: 1 near the eye, 0 beyond MARCH_FAR_M.
float wSurfaceY(vec2 p, vec2 gx, vec2 gy, float fade) {
  // Open water, deeper than the beach waves reach (and the slab is deep): the height is the two big tiles at full weight and nothing else (no beach phase, no
  // swash, and the ground cannot show through). Same result as the full path below, for one fetch of the terrain instead of about twenty.
  if (uSeaY - wGroundMax3(p) > ${DEEP_SKIP_M.toFixed(2)}) {
    float e = 0.0;
    if (fade > 0.0 && uFFT > 0.5) {
      e += wTileEta(uSA0, uTileL.x, uTO0, 1.0, p, gx, gy);
      if (uTiles > 1.5) e += wTileEta(uSA1, uTileL.y, uTO1, 1.0, p, gx, gy);
    }
    return uSeaY + clamp(e * fade, -uHlow, uHtop);
  }
  float ground = wGround(p);
  float dS = uSeaY - ground;
  float eta = 0.0;
  if (fade > 0.0) {
    float ws = uShoreOn * wShoreIn(p) * (1.0 - smoothstep(${SURF_D0.toFixed(2)}, ${SURF_D1.toFixed(2)}, dS));
    float sh = smoothstep(0.02, 0.8, dS);
    if (uFFT > 0.5) {
      eta += wTileEta(uSA0, uTileL.x, uTO0, (1.0 - ws) * sh, p, gx, gy);
      if (uTiles > 1.5) eta += wTileEta(uSA1, uTileL.y, uTO1, sh, p, gx, gy);
    }
    if (ws > 0.0 && dS > 0.0) {
      vec4 shr = wShoreAt(p);
      float ratio = uOmega / uOmegaRef;
      float theta = shr.x * ratio + uPhi + wDphi(p);
      float cyc = theta / 6.2831853, iw = floor(cyc), u = cyc - iw;
      float d = max(dS, 0.02);
      vec2 nS = shr.yz / max(length(shr.yz), 1e-5);
      float expo = mix(0.30, 1.0, smoothstep(-0.4, 0.7, dot(nS, -uWaveDir)));
      WBore B = wBoreParams(iw, p, d, wKs(d) * expo);
      eta += ws * smoothstep(0.0, 0.08, dS) * wBoreEta(u, B);
    }
    eta = clamp(eta * fade, -uHlow, uHtop);
  }
  float edge, a;
  // The film is at most 0.03 m thick and lies on the ground: with the ground more than that below the slab's floor it cannot reach the surface.
  float film = (fade > 0.0 && ground - uSeaY > -(uHlow + 0.05)) ? wSwash(p, ground - uSeaY, edge, a) * fade : 0.0;
  return max(uSeaY + eta, ground + film);
}
`;

// Reflection of a distant light off the wave facets. L: toward the light, K: its strength, N: the
// facet normal, V: toward the eye, m2: squared width of the lobe (the sum of the slope variances the normal does not carry). Returns display-range colour.
export const WATER_GLITTER = /* glsl */`
vec3 wGlitter(vec3 L, vec3 K, vec3 N, vec3 V, float m2) {
  if (K.r + K.g + K.b <= 0.0 || L.y <= 0.0) return vec3(0.0);
  vec3 H = normalize(L + V);
  float nh = clamp(dot(N, H), 1e-3, 1.0);
  float t2 = (1.0 - nh * nh) / (nh * nh);
  float D = exp(-t2 / m2) / (3.14159265 * m2 * nh * nh * nh * nh);
  float vh = clamp(dot(V, H), 0.0, 1.0);
  float F = 0.02 + 0.98 * pow(1.0 - vh, 5.0);
  float up = smoothstep(0.0, 0.06, L.y);                    // fades out as the light meets the horizon
  return 1.0 - exp(-K * (D * F * up * ${GLITTER_GAIN.toFixed(3)}));
}
`;

const WATER_COLOR = /* glsl */`
#include <color_fragment>
vec3 wHit = vWPos;
float wDepth;
{
  vec3 P0 = vWPos;
  vec3 ray = normalize(P0 - uEyeRel);
  vec2 gx = dFdx(P0.xz), gy = dFdy(P0.xz);              // the pixel's footprint on the water (for the mip levels)
  float d0 = distance(uEyeRel, P0);
  float tHit = (uSeaY - P0.y) / min(ray.y, -1e-4);       // along the ray from the plane to the mean level
  float gnd;
  if (uMarch > 0.5 && uUnder < 0.5 && d0 < ${MARCH_FAR_M.toFixed(1)}) {
    // March the ray down through the slab to its first meeting with the water surface or the ground.
    float tb = (uSeaY - uHlow - P0.y) / min(ray.y, -1e-4);
    // Coarse steps: the quality level's count near the eye, fewer with distance (the far surface is mip-filtered smooth). Then as many bisections
    // as it takes to place the hit to a fraction of the pixel's footprint.
    float nMax = mix(uMarch, max(uMarch * ${MARCH_FAR_FRAC.toFixed(2)}, ${MARCH_MIN_STEPS.toFixed(1)}), smoothstep(${MARCH_STEP_D0.toFixed(1)}, ${MARCH_STEP_D1.toFixed(1)}, d0));
    float nStep = min(max(floor(nMax + 0.5), ${MARCH_MIN_STEPS.toFixed(1)}), uMarch);
    float dt = tb / nStep;
    float epsT = max(0.01, 0.25 * max(length(gx), length(gy)));
    float nBis = clamp(ceil(log2(dt / epsT)), 2.0, 6.0);
    float tPrev = 0.0;
    float t1 = tb;
    bool found = false;
    for (int i = 1; i <= 40; i++) {
      if (float(i) > nStep) break;
      float t = dt * float(i);
      vec3 P = P0 + ray * t;
      float fade = 1.0 - smoothstep(${MARCH_NEAR_M.toFixed(1)}, ${MARCH_FAR_M.toFixed(1)}, distance(uEyeRel, P));
      if (P.y <= wSurfaceY(P.xz, gx, gy, fade)) { t1 = t; found = true; break; }
      tPrev = t;
    }
    if (found) {
      float lo = tPrev, hi = t1;
      // In shallow water the hit decides where the waterline is: a few mm along the ray is centimetres along a gently sloping beach, and the 1.5 mm depth
      // cut-off shows it. There the bisection keeps the precision of the old fixed 16 steps and 6 halvings (a 1/1024 of the path); only deep water relaxes it.
      float nB = nBis;
      if (uSeaY - wGroundMax3((P0 + ray * t1).xz) < ${SHALLOW_FULL_M.toFixed(1)}) nB = max(nB, clamp(ceil(log2(1024.0 / nStep)), 6.0, 8.0));
      for (int k = 0; k < 8; k++) {
        if (float(k) >= nB) break;
        float tm = 0.5 * (lo + hi);
        vec3 P = P0 + ray * tm;
        float fade = 1.0 - smoothstep(${MARCH_NEAR_M.toFixed(1)}, ${MARCH_FAR_M.toFixed(1)}, distance(uEyeRel, P));
        if (P.y <= wSurfaceY(P.xz, gx, gy, fade)) hi = tm; else lo = tm;
      }
      tHit = 0.5 * (lo + hi);
    } else tHit = tb;
  }
  wHit = P0 + ray * tHit;
  gnd = wGround(wHit.xz);
  float fadeH = (uMarch > 0.5 && uUnder < 0.5) ? 1.0 - smoothstep(${MARCH_NEAR_M.toFixed(1)}, ${MARCH_FAR_M.toFixed(1)}, distance(uEyeRel, wHit)) : 0.0;
  float surfY = (uMarch > 0.5 && uUnder < 0.5) ? wSurfaceY(wHit.xz, gx, gy, fadeH) : uSeaY;
  wDepth = surfY - gnd;
  if (wDepth <= 0.0015) discard;                      // dry land, or the sand before the swash has reached it
}
{
  vec2 wp = wHit.xz;
  float dist = distance(uEyeRel, wHit);
  vec3 wVv = normalize(uEyeRel - wHit);
  float fp = max(length(dFdx(wp)), length(dFdy(wp)));     // the pixel's footprint on the water, m
  vec2 gx = dFdx(vWPos.xz), gy = dFdy(vWPos.xz);
  float ground = wGround(wp);
  float dS = uSeaY - ground;                             // still-water depth (negative on the dry part of the swash)

  // Beach waves: weight, phase, profile.
  float ws = uShoreOn * wShoreIn(wp) * (1.0 - smoothstep(${SURF_D0.toFixed(2)}, ${SURF_D1.toFixed(2)}, dS));
  vec2 bSlope = vec2(0.0); float boreFoam = 0.0, bEta = 0.0; vec2 tdir = uWaveDir;
  if (ws > 0.0 && dS > 0.0) {
    vec4 shr = wShoreAt(wp);
    float ratio = uOmega / uOmegaRef;
    vec2 gth = shr.yz * ratio;                            // gradient of the phase, rad/m
    float theta = shr.x * ratio + uPhi + wDphi(wp);
    float cyc = theta / 6.2831853, iw = floor(cyc), u = cyc - iw;
    float d = max(dS, 0.02);
    vec2 nS = shr.yz / max(length(shr.yz), 1e-5);
    float expo = mix(0.30, 1.0, smoothstep(-0.4, 0.7, dot(nS, -uWaveDir)));
    WBore B = wBoreParams(iw, wp, d, wKs(d) * expo);
    float e0 = wBoreEta(u, B);
    float du = 0.004, u1 = min(u + du, 0.9999), u0 = max(u - du, 0.0);
    float dEta = (wBoreEta(u1, B) - wBoreEta(u0, B)) / max(u1 - u0, 1e-4);
    float wsd = ws * smoothstep(0.0, 0.08, dS);
    bEta = e0 * wsd;
    bSlope = gth * (dEta / 6.2831853) * wsd;
    tdir = -nS;
    // foam: the roller on the bore's front, and what it leaves behind, thinning to lace with the time since it passed
    float T = 6.2831853 / max(uOmega, 0.1);
    float cov = 1.45 * B.bn * smoothstep(1.0 - 2.0 * B.wn, 1.0, u);                       // on the face of the next wave
    cov = max(cov, 1.15 * B.ba * exp(-u * T / 1.4));                                        // behind this one
    // streaky noise in a fixed frame (along and across the swell's heading), fine octave fading out as the pixel outgrows it
    vec2 wpA = wp + uOriginXZ;
    vec2 q = vec2(dot(wpA, uWaveDir) * 0.5, dot(wpA, vec2(-uWaveDir.y, uWaveDir.x)));
    float fa = 1.0 - smoothstep(0.015, 0.06, fp);
    float n = 0.36 * wNoise(q * 2.7) + 0.30 * wNoise(q * 7.1 + 3.3) + 0.34 * mix(0.5, wNoise(q * 21.0 + 7.7), fa);
    n = 0.5 + (n - 0.5) * 1.8;                                                          // value noise clusters round its mean: stretch it
    float lacy = smoothstep(0.55, 0.0, cov);                                          // the thinner the foam, the more the noise decides
    boreFoam = smoothstep(n - 0.07 - 0.08 * (1.0 - lacy), n + 0.05, cov * 1.10) * wsd;
  }

  // Wave slopes: the sum of the tiles' mean slopes, and the variance of what each tile's mip level lost.
  vec2 S = vec2(0.0); vec3 V2 = vec3(0.0); float eta = 0.0, f0 = 0.0, f1 = 0.0, f2 = 0.0;
  float gust = uGustOn > 0.5 ? (0.30 + 0.70 * windGust(wp + uOriginXZ)) / 0.65 : 1.0;   // local wind over the mean
  float shallow = smoothstep(0.02, 0.8, wDepth);                                // waves die out in the last few decimetres
  float chop = shallow * (1.0 - 0.6 * boreFoam);
  if (uFFT > 0.5) {
    wTile(uSA0, uSB0, uTileL.x, uTO0, (1.0 - ws) * shallow, wp, gx, gy, 1.0, S, V2, eta, f0);
    if (uTiles > 1.5) wTile(uSA1, uSB1, uTileL.y, uTO1, chop * pow(gust, 0.5), wp, gx, gy, 1.0, S, V2, eta, f1);
    if (uTiles > 2.5) wTile(uSA2, uSB2, uTileL.z, uTO2, chop * pow(gust, 1.2), wp, gx, gy, 0.0, S, V2, eta, f2);
  } else {
    float var = 0.0;
    S = wFallback(wp + uOriginXZ, uWaveDir, uWT, fp, var) * shallow;
    V2 = vec3(var, var, 0.0);
  }
  S += bSlope;
  wRing = 0.0;
  if (uRainN > 0.5 && wDepth > 0.02) {
    float rr = wRainRate(wp);
    if (rr > 0.3) {
      float k = smoothstep(0.02, 0.1, wDepth);
      float dens = clamp(rr / 14.0, 0.0, 1.0);
      V2.xy += vec2(0.0025 * dens * k);                                      // the drops roughen the surface all the way out
      if (fp < 0.05) {
        vec2 rs = wRainSlope(wp, rr, uWT) * (1.0 - smoothstep(0.02, 0.05, fp)) * k;
        S += 3.5 * rs;
        wRing = min(length(rs) * 3.0, 1.0);
      }
    }
  }
  wSig2 = 0.5 * (V2.x + V2.y);
  wEta = eta + bEta;
  wN = normalize(vec3(-S.x, 1.0, -S.y));
  if (!gl_FrontFacing) wN = -wN;

  // Colour and opacity by depth.
  float depthT = 1.0 - exp(-wDepth * ${ABSORB_COLOUR.toFixed(2)});
  vec3 body = mix(uShallow, uDeep, pow(depthT, 0.8));
  body += uSky * (0.30 * wRing);                                           // rain rings catch the sky
  float alpha = (1.0 - exp(-wDepth * ${ABSORB_ALPHA.toFixed(2)})) * smoothstep(0.0, 0.012, wDepth);

  // Sunlight through the waves: crests facing away from the sun, seen from the sun's far side, glow green.
  {
    float sunOn = step(1e-6, uSunK.r + uSunK.g + uSunK.b) * step(0.0, uSunDir.y);
    float crest = smoothstep(-0.3, 1.0, wEta / (0.5 * uHs + 0.03));
    float through = pow(max(dot(normalize(uSunDir + 0.35 * wN), -wVv), 0.0), 3.0);
    body += uScatter * through * (0.15 + 0.85 * crest) * sunOn * smoothstep(0.3, 1.5, wDepth);
  }

  // Foam: white caps from the tiles' folded crests, the bore's foam, and the swash's edge.
  float faL = 1.0 - smoothstep(0.02, 0.08, fp);
  float lace = wNoiseO(wp * 5.0 + vec2(uWT * 0.3, 0.0), uNC5) * 0.6 + mix(0.5, wNoiseO(wp * 17.0 - vec2(0.0, uWT * 0.2), uNC17), faL) * 0.4;
  float capT = clamp(f0 * 0.9 + f1 + f2 * 0.5, 0.0, 1.2);
  wCapFoam = smoothstep(0.30, 0.80, capT * 1.25 + (lace - 0.5) * 0.7) * smoothstep(0.15, 0.6, wDepth);
  float sEdge = 0.0, sa = 0.0;
  float film = wSwash(wp, ground - uSeaY, sEdge, sa);
  float tipFoam = sEdge * smoothstep(0.0, 0.01, film + 0.002);
  float nT = 0.5 * wNoiseO(wp * 6.0, uNC6) + 0.5 * mix(0.5, wNoiseO(wp * 17.0 + 4.0, uNC17), faL);
  tipFoam *= smoothstep(0.25, 0.7, nT + 0.3);
  wFoam = clamp(max(max(boreFoam, wCapFoam), tipFoam), 0.0, 1.0);
  diffuseColor.rgb = mix(body, vec3(0.72 + 0.24 * lace), wFoam);
  diffuseColor.a = max(alpha, wFoam * 0.92 * smoothstep(0.0, 0.01, wDepth));
}
`;

const WATER_ROUGH = /* glsl */`
float roughnessFactor = mix(0.045, 0.9, wFoam);
`;

const WATER_NORMAL = /* glsl */`
#include <normal_fragment_begin>
normal = normalize((viewMatrix * vec4(wN, 0.0)).xyz);
`;

const WATER_POST = /* glsl */`
#include <tonemapping_fragment>
{
  vec3 wV = normalize(uEyeRel - wHit);
  if (!gl_FrontFacing || uUnder > 0.5) {
    // The underside of the surface: a dark ceiling with a bright window where the sky shows
    // through (Snell: about 48 degrees from straight up), mirror-dark beyond it.
    float win = smoothstep(0.62, 0.74, -wV.y);
    vec3 ceilingCol = mix(uUnderCol * 0.8, uSky * vec3(0.75, 0.95, 1.0), win);
    gl_FragColor = vec4(ceilingCol, 0.96);
  } else {
    // The reflection is as wide as the slopes the normal does not carry (a facet's tilt of s turns the reflected ray by 2 s).
    float lobe = 2.0 * sqrt(wSig2 + ${PROBE_FLOOR.toExponential(2)});
    float lod = clamp(log2(lobe * ${CUBE_RES.toFixed(1)} / 1.5708), 0.0, 6.0);
    float nv = max(dot(wN, wV), 0.5 * sqrt(wSig2));
    float fr = 0.025 + 0.975 * pow(1.0 - clamp(nv, 0.0, 1.0), 5.0);
    // The sky in the reflected direction, and the sky at the horizon in the view direction (the fog's colour).
    vec3 wR = reflect(-wV, wN);
    vec3 rDir = normalize(vec3(wR.x, max(wR.y, 0.01), wR.z));
    vec3 skyRefl = uSkyCube_on(rDir, lod);
    // the stars and the Milky Way in the reflection, screen-blended onto it as they are onto the sky itself
    skyRefl += (vec3(1.0) - skyRefl) * clamp(uStarCube_on(rDir, lod) * ${STAR_REFL_GAIN.toFixed(2)}, 0.0, 1.0);
    vec3 skyFog  = uSkyCube_on(normalize(vec3(-wV.x, 0.02, -wV.z)), ${FOG_LOD.toFixed(1)});
    gl_FragColor.rgb = mix(gl_FragColor.rgb, skyRefl, fr * 0.85 * (1.0 - wFoam));
    gl_FragColor.a = max(gl_FragColor.a, fr * 0.9);
    // sun and moon glitter, screen-blended on top (foam is matte and takes little of it)
    float wDist = distance(uEyeRel, wHit);
    float m2 = 2.0 * wSig2 + ${GLITTER_FLOOR.toExponential(2)};
    vec3 gl = (wGlitter(uSunDir, uSunK, wN, wV, m2) + wGlitter(uMoonDir, uMoonK, wN, wV, m2)) * (1.0 - 0.85 * wFoam);
    gl_FragColor.rgb += (vec3(1.0) - gl_FragColor.rgb) * clamp(gl, 0.0, 1.0);
    gl_FragColor.a = max(gl_FragColor.a, max(gl.r, max(gl.g, gl.b)));
    // Distance fog toward the sky behind the water, drawn here (the material's own fog is off) so that it
    // goes to the colour of the sky in this direction and not the average round the compass.
    vec3 airT = airTransmit(uEyeRel, (wHit - uEyeRel) / max(wDist, 1e-3), wDist);       // the air between the eye and this water (air.ts)
    gl_FragColor.rgb = gl_FragColor.rgb * airT + skyFog * (1.0 - airT);
    float ff = 1.0 - exp(-uFogDensity * uFogDensity * wDist * wDist);
    gl_FragColor.rgb = mix(gl_FragColor.rgb, skyFog, ff);
  }
  gl_FragColor.a *= uNearFade;
}
`;

/** The two lights that glitter on the sea. Directions are unit vectors in three's axes (y up); K is
 *  SKY_EXPOSURE * the display gain * the beam's per-channel irradiance (the renderer's sky curve). */
export interface WaterLights {
  sunDir: THREE.Vector3; sunK: ArrayLike<number>;
  moonDir?: THREE.Vector3 | null; moonK?: ArrayLike<number> | null;
}

/** What the sea costs, by level: wave texels per tile side, tiles, frames per FFT update, and ray-march steps (0 = a flat plane). */
const QUALITY = [
  { n: 256, cascades: 3, every: 1, march: 16 },
  { n: 256, cascades: 3, every: 2, march: 10 },
  { n: 128, cascades: 3, every: 2, march: 0 },
];

/** Group speed of a wave of angular frequency w in depth d (m/s). */
function groupSpeed(w: number, d: number): number {
  const k = hunt(w, Math.max(d, 0.05)), kd = Math.min(k * d, 20);
  return 0.5 * w / k * (1 + 2 * kd / Math.sinh(2 * kd));
}

export class WaterView {
  readonly mesh: THREE.Mesh;
  private readonly scene: THREE.Scene;
  private readonly geo: THREE.BufferGeometry;
  private readonly material: THREE.MeshStandardMaterial;
  private readonly ground: GroundUniforms;
  private shore: ShoreField | null = null;
  private shoreVersion = -1;
  private ocean: OceanFFT | null = null;
  private oceanTried = false;
  private quality = 0;
  private frame = 0;
  private accDt = 0;
  private reachM = PLANE_M * 0.5;       // half side of the square the sea covers (setReach)
  private phi = 0;
  private rainSrc = new Float64Array(24);
  private rainN = 0;
  private rainVer = -1;
  private lastT = -1;
  private sea: SeaState = { ...DEFAULT_SEA };
  private stats: SeaStats = seaStats(DEFAULT_SEA);
  // The wave statistics (hs, hsLong, mss ...) cost some 50 ms to sum over every wave mode, so they are not worked out per frame: a job (SeaStatsJob) sums them a
  // millisecond or so a frame, a new one starts only when the sea state has moved enough from the one the last result was made for, and at most every STATS_MIN_GAP_S.
  // The result lags the state by a second or two, against a relaxation that takes tens of seconds. setSeaState (a snap) still works them out at once.
  private statsFor: SeaState = { ...DEFAULT_SEA };
  private statsJob: SeaStatsJob | null = null;
  private statsAt = -1e9;
  private windU: { uWindT: { value: number }; uWindDir: { value: THREE.Vector2 }; uWindAmp: { value: number } } | null = null;
  private readonly u = {
    uSeaY:      { value: 0 },
    uWT:        { value: 0 },
    uWaveDir:   { value: new THREE.Vector2(0.80, -0.60).normalize() },
    uShallow:   { value: new THREE.Vector3(...SHALLOW) },
    uDeep:      { value: new THREE.Vector3(...DEEP) },
    uScatter:   { value: new THREE.Vector3(...SCATTER) },
    uSky:       { value: new THREE.Vector3(0.5, 0.6, 0.8) },
    uSkyCube:   { value: null as THREE.Texture | null },
    uSkyCubeOn: { value: 0 },
    uStarCube:  { value: null as THREE.Texture | null },
    uStarCubeOn:{ value: 0 },
    uFogDensity:{ value: 0 },
    ...air.uniforms,
    uUnderCol:  { value: new THREE.Vector3(0.02, 0.2, 0.25) },
    uUnder:     { value: 0 },
    uNearFade:  { value: 1 },
    uSunDir:    { value: new THREE.Vector3(0, 1, 0) },
    uSunK:      { value: new THREE.Vector3() },
    uMoonDir:   { value: new THREE.Vector3(0, 1, 0) },
    uMoonK:     { value: new THREE.Vector3() },
    uSA0: { value: null as THREE.Texture | null }, uSA1: { value: null as THREE.Texture | null }, uSA2: { value: null as THREE.Texture | null },
    uSB0: { value: null as THREE.Texture | null }, uSB1: { value: null as THREE.Texture | null }, uSB2: { value: null as THREE.Texture | null },
    uTileL:     { value: new THREE.Vector3(CASCADE_L[0], CASCADE_L[1], CASCADE_L[2]) },
    uTiles:     { value: 3 },
    uFFT:       { value: 0 },
    uHs:        { value: 0.3 },
    uGustOn:    { value: 0 },
    uShore:     { value: null as THREE.Texture | null },
    uShoreInfo: { value: new THREE.Vector4(2, 2, 1, 1) },
    uShoreOrg:  { value: new THREE.Vector2() },
    uShoreOn:   { value: 0 },
    uPhi:       { value: 0 },
    uOmega:     { value: 2.5 },
    uOmegaRef:  { value: OMEGA_REF },
    uHinc:      { value: 0.2 },
    uCgOff:     { value: 3 },
    uMarch:     { value: QUALITY[0].march },
    uHtop:      { value: 0.4 },
    uHlow:      { value: 0.3 },
    uSwashT:    { value: 2.5 },
    uRain:      { value: Array.from({ length: 6 }, () => new THREE.Vector4(0, 0, 1, 0)) },
    uRainN:     { value: 0 },
    ...origin.uniforms,
    uMeshRel:   { value: new THREE.Vector3() },
    uTO0: { value: new THREE.Vector2() }, uTO1: { value: new THREE.Vector2() }, uTO2: { value: new THREE.Vector2() },
    uNC5: { value: new THREE.Vector2() }, uNC6: { value: new THREE.Vector2() }, uNC17: { value: new THREE.Vector2() }, uNCR: { value: new THREE.Vector2() },
    uWindT:     { value: 0 },
    uWindDir:   { value: new THREE.Vector2(0.8, -0.6) },
    uWindAmp:   { value: 1 },
  };

  /** `ground`: the ground clipmap's uniforms (groundClip.ts): the sea's depth is read from them. */
  constructor(scene: THREE.Scene, ground: GroundUniforms) {
    this.scene = scene;
    this.ground = ground;
    // A disc of rings that grow with distance (planet.ts): the vertex stage bends it with the rest of the world, which a two-triangle quad could not follow. It covers the square of side PLANE_M
    // (its corners are PLANE_M / sqrt(2) from the middle), and setReach grows it as it grew the quad. Lies flat on the XZ plane.
    this.geo = curvedDisc(PLANE_M * Math.SQRT1_2);
    const m = new THREE.MeshStandardMaterial({
      color: 0xffffff, roughness: 0.05, metalness: 0.0,
      transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false,
    });
    m.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, this.u, this.ground);
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vWPos;\nuniform vec3 uMeshRel;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nvWPos = (modelMatrix * vec4(transformed, 0.0)).xyz + uMeshRel;');   // the mesh's own offset from O (a double sum, origin.ts)
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\n' + WATER_COMMON + WATER_GLITTER)
        .replace('#include <color_fragment>', WATER_COLOR)
        .replace('#include <roughnessmap_fragment>', WATER_ROUGH)
        .replace('#include <normal_fragment_begin>', WATER_NORMAL)
        .replace('#include <tonemapping_fragment>', WATER_POST);
    };
    m.customProgramCacheKey = () => 'water-v11-clipmap';
    this.material = m;
    this.mesh = new THREE.Mesh(this.geo, m);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.renderOrder = 2;                       // after the opaque scene, so the shallows blend over the sand
    this.mesh.visible = false;                       // shown once there is terrain with a sea
    this.u.uShoreOn.value = 0;
    scene.add(this.mesh);
    this.refreshSea(false);                          // the statistics of the default sea were made with the field above
  }

  /**
   * The shore window (the server's 512 x 512 window of 2.56 m cells, groundClip.ts `shore()`): solves the beach waves' phase over it. Null (the eye is high, or it has not come) puts the beach waves
   * out. The sea's depth itself comes from the ground clipmap's uniforms, not from here. `seaLevel` is the still-water level, without the tide.
   */
  setShore(w: ShoreWindow | null, seaLevel = 0) {
    if (w ? w.version === this.shoreVersion : this.shoreVersion === -1) return;     // nothing new
    this.shoreVersion = w ? w.version : -1;
    if (this.shore) { this.shore.tex.dispose(); this.shore = null; }
    const f = w?.field;
    if (w && f && f.nx > 3 && f.ny > 3) {
      this.shore = buildShoreField(f, seaLevel);
      this.u.uShore.value = this.shore.tex;
      this.u.uShoreInfo.value.set(this.shore.nx, this.shore.ny, this.shore.width, this.shore.height);
      this.u.uShoreOrg.value.set(w.x0, w.z0);
      this.u.uShoreOn.value = 1;
      this.sea = { ...this.sea, depth: Math.max(0.5, this.shore.depthOff) };
    } else { this.u.uShoreOn.value = 0; }
    this.refreshSea(false);                          // a new depth is picked up by the statistics job (pumpStats)
  }

  /**
   * How far the sea reaches from the eye, metres (the half side of the square it covers). The renderer gives the camera's far plane: the sea then ends where nothing is drawn any more,
   * so it has no edge of its own to show. The waves are made per pixel from world coordinates and the pixel's footprint, so a bigger plane costs nothing more than the pixels it covers.
   */
  /** How opaque the sea is (1 near the ground; falling to 0 as the eye leaves the planet, bodies.ts nearFade): the planet the far shell draws under it shows through. */
  setNearFade(v: number) {
    const f = Math.min(1, Math.max(0, v));
    this.u.uNearFade.value = f;
    this.nearFade = f;
  }
  /** The last value given to setNearFade (the renderer hides the sea when it is 0). */
  nearFade = 1;

  setReach(halfSideM: number) {
    this.reachM = Number.isFinite(halfSideM) && halfSideM > 0 ? halfSideM : PLANE_M * 0.5;
  }

  /** Show or hide the sea (hidden for worlds with no ground below sea level, and by a debug toggle). */
  setEnabled(e: boolean) { this.mesh.visible = e; }

  /** The sky's probe cube (Sky.probe.texture), or null before it has been drawn: what the sea reflects and fogs into. */
  setSkyProbe(tex: THREE.Texture | null) {
    this.u.uSkyCube.value = tex;
    this.u.uSkyCubeOn.value = tex ? 1 : 0;
  }

  /** The star cube (StarProbe.target.texture), or null by day and before it has been drawn: the stars and the Milky Way in the reflection. */
  setStarProbe(tex: THREE.Texture | null) {
    this.u.uStarCube.value = tex;
    this.u.uStarCubeOn.value = tex ? 1 : 0;
  }

  /** Bind the shared wind (wind.ts WindField.uniforms): its gusts roughen the short waves where they cross the water. */
  setWind(w: { uniforms: { uWindT: { value: number }; uWindDir: { value: THREE.Vector2 }; uWindAmp: { value: number } } } | null) {
    this.windU = w ? w.uniforms : null;
    this.u.uGustOn.value = w ? 1 : 0;
  }

  /** The sea state (wind speed at 10 m in m/s, direction the waves travel toward as an angle in the three x-z plane, fetch and depth in metres). */
  setSeaState(s: Partial<SeaState>) {
    this.sea = { ...this.sea, ...s };
    this.refreshSea(true);                    // a snap: the statistics are worked out now (about 50 ms), once
  }
  get seaState(): SeaState { return { ...this.sea }; }

  /**
   * Let the sea state follow a target: the wave field takes tens of seconds to grow with a rising wind and longer to die away, and turns
   * the short way round when the wind veers. Call once per frame with the frame's dt; only the fields given are followed.
   */
  relaxSea(target: Partial<SeaState>, dt: number, upS = 30, downS = 45) {
    const s = this.sea, n: Partial<SeaState> = {};
    if (target.speed !== undefined) {
      const tau = target.speed > s.speed ? upS : downS;
      n.speed = s.speed + (target.speed - s.speed) * (1 - Math.exp(-dt / tau));
    }
    if (target.dir !== undefined) {
      let d = target.dir - s.dir; d = Math.atan2(Math.sin(d), Math.cos(d));
      n.dir = s.dir + d * (1 - Math.exp(-dt / 20));
    }
    if (target.fetch !== undefined) n.fetch = s.fetch + (target.fetch - s.fetch) * (1 - Math.exp(-dt / 60));
    // Swell: each train follows its target in height, period and heading (a train with no partner grows from nothing, or dies away to nothing, over the same time). Far storms
    // change over hours, so the relaxation only has to hide the server's steps.
    let swellMoved_ = false;
    if (target.swell !== undefined) {
      const k = 1 - Math.exp(-dt / SWELL_TAU_S), cur = s.swell ?? [], out: SwellTrain[] = [];
      for (let i = 0; i < Math.max(cur.length, target.swell.length); i++) {
        const c = cur[i], t = target.swell[i];
        if (c && t) {
          out.push({ hs: c.hs + (t.hs - c.hs) * k, tp: c.tp + (t.tp - c.tp) * k, dir: c.dir + wrapAngle(t.dir - c.dir) * k });
        } else if (t) {
          out.push({ hs: t.hs * k, tp: t.tp, dir: t.dir });
        } else if (c) {
          const hs = c.hs * (1 - k);
          if (hs > 0.02) out.push({ hs, tp: c.tp, dir: c.dir });
        }
      }
      if (swellMoved(out, s.swell)) { n.swell = out; swellMoved_ = true; }
    }
    if (n.speed === undefined && n.dir === undefined && n.fetch === undefined && !swellMoved_) return;
    if ((n.speed ?? s.speed) === s.speed && (n.dir ?? s.dir) === s.dir && (n.fetch ?? s.fetch) === s.fetch && !swellMoved_) return;
    this.sea = { ...this.sea, ...n };
    this.refreshSea(false);                   // every frame while the wind relaxes: only the cheap terms; the statistics follow in slices (pumpStats)
  }

  /** Raining discs near the eye, packed (x, z, radius, mm/h) per disc; `n` of them (at most 6) go to the ripples. 0 turns the ripples off. */
  setRain(discs: ArrayLike<number> | null, n: number) {
    n = discs ? Math.min(6, n | 0) : 0;
    for (let i = 0; i < n * 4; i++) this.rainSrc[i] = discs![i]!;
    this.rainN = n;
    this.applyRain();
  }
  /** The discs' centres are held in the world and given to the shader counted from O (redone when O moves). */
  private applyRain() {
    const n = this.rainN, a = this.rainSrc;
    for (let i = 0; i < n; i++) this.u.uRain.value[i]!.set(a[i * 4]! - origin.org.x, a[i * 4 + 1]! - origin.org.y, a[i * 4 + 2]!, a[i * 4 + 3]!);
    this.u.uRainN.value = n;
    this.rainVer = origin.version;
  }
  get seaStats(): SeaStats { return this.stats; }
  /** Height the swash reaches above the still level, m (for the wet band on the sand). */
  get runup(): number { return 0.30 * Math.min(this.stats.hsLong * 1.1, 0.55); }

  /** Whether the sea state has moved far enough from the one the statistics were made for to be worth working them out again. */
  private statsStale(): boolean {
    const a = this.sea, b = this.statsFor;
    return Math.abs(a.speed - b.speed) > STATS_MOVE * Math.max(b.speed, 1)
      || Math.abs(a.fetch - b.fetch) > STATS_MOVE * Math.max(b.fetch, 100)
      || Math.abs(a.depth - b.depth) > STATS_MOVE * Math.max(b.depth, 0.5)
      || swellMoved(a.swell, b.swell);
  }

  /** Per frame: give the running statistics job its slice, take its result when it finishes, start another when the state has moved. */
  private pumpStats(tSec: number) {
    if (this.statsJob) {
      if (this.statsJob.run(STATS_SLICE_MS)) {
        this.stats = this.statsJob.result; this.statsFor = this.statsJob.state; this.statsJob = null; this.statsAt = tSec;
        this.applyStats();
      }
    } else if (tSec - this.statsAt >= STATS_MIN_GAP_S && this.statsStale()) {
      this.statsJob = new SeaStatsJob(this.sea);
    }
  }

  private refreshSea(now = true) {
    if (now) { this.stats = seaStats(this.sea); this.statsFor = { ...this.sea }; this.statsJob = null; }
    this.applyStats();
  }

  /** The uniforms that follow the sea state: the peak frequency and direction are exact each time, the statistics are those of the last finished job. */
  private applyStats() {
    const U = this.u, st = this.stats;
    // The beach waves are one train: its frequency and heading are those of the sea's energy, wind sea and swell weighted by their variance (frequency as a log mean, heading as a
    // vector mean), so a big swell sets the crest spacing and heading the surf zone shows and a rising wind sea takes over smoothly.
    const sw = this.sea.swell ?? [];
    let eS = 0; for (const t of sw) eS += t.hs * t.hs;
    const eW = Math.max(0, st.hs * st.hs - eS);
    let lw = eW * Math.log(peakOmega(this.sea)), ex = eW * Math.cos(this.sea.dir), ey = eW * Math.sin(this.sea.dir), et = eW;
    for (const t of sw) { const e = t.hs * t.hs; lw += e * Math.log(2 * Math.PI / Math.max(t.tp, 2)); ex += e * Math.cos(t.dir); ey += e * Math.sin(t.dir); et += e; }
    const w = et > 1e-9 ? Math.exp(lw / et) : peakOmega(this.sea);
    const dm = Math.hypot(ex, ey);
    const dir = dm > 1e-6 ? Math.atan2(ey, ex) : this.sea.dir;
    U.uHs.value = st.hs;
    U.uWaveDir.value.set(Math.cos(dir), Math.sin(dir));
    U.uOmega.value = w;
    U.uSwashT.value = 2 * Math.PI / w;
    U.uHinc.value = st.hsLong;
    U.uCgOff.value = groupSpeed(w, this.sea.depth);
    const top = Math.min(3.0, Math.max(0.28, 1.0 * st.hs + 0.15));
    U.uHtop.value = top; U.uHlow.value = 0.6 * top;
    this.applyFoam();
  }

  /** Sea cost level: 0 full, 1 half the FFT updates and a shorter ray march, 2 also a 128-texel grid and a flat plane (no ray march). */
  setQuality(q: number) {
    q = Math.max(0, Math.min(QUALITY.length - 1, Math.round(q)));
    if (q === this.quality) return;
    const a = QUALITY[this.quality], b = QUALITY[q];
    this.quality = q;
    this.u.uMarch.value = b.march;
    if (this.ocean && (a.n !== b.n || a.cascades !== b.cascades)) { this.ocean.dispose(); this.ocean = null; this.oceanTried = false; }
  }

  private applyFoam() {
    if (!this.ocean) return;
    const U = this.sea.speed;
    this.ocean.foamBias = 0.78;
    this.ocean.foamOn = Math.min(1, Math.max(0, (U - 3.0) / 3.0));    // no white caps below a light breeze
  }

  /** Per frame, once, outside the main render: advance the wave FFT. */
  simulate(renderer: THREE.WebGLRenderer, tSec: number, dt: number) {
    if (!this.mesh.visible) return;
    if (!this.ocean && !this.oceanTried) {
      this.oceanTried = true;
      if (OceanFFT.supported(renderer)) {
        const q = QUALITY[this.quality];
        this.ocean = new OceanFFT({ n: q.n, cascades: q.cascades });
        this.ocean.setState(this.sea);
        this.applyFoam();
        this.u.uTiles.value = q.cascades;
        this.u.uSA0.value = this.ocean.texA(0); this.u.uSA1.value = q.cascades > 1 ? this.ocean.texA(1) : null; this.u.uSA2.value = q.cascades > 2 ? this.ocean.texA(2) : null;
        this.u.uFFT.value = 0;                     // until the first update has filled the tiles
      }
    }
    const o = this.ocean;
    if (!o) return;
    this.accDt += dt;
    if (++this.frame % QUALITY[this.quality].every === 0 || this.u.uFFT.value < 0.5) {
      o.setState(this.sea);
      o.update(renderer, tSec, this.accDt);
      this.accDt = 0;
      this.u.uSB0.value = o.texB(0); if (o.count > 1) this.u.uSB1.value = o.texB(1); if (o.count > 2) this.u.uSB2.value = o.texB(2);
      this.u.uFFT.value = 1;
    }
  }

  /**
   * Per frame. `seaY`: three-y of the surface (sea level plus tide). `sky`: the display-range sky
   * colour. `under`: the eye is below the surface. `underCol`: the colour of the water seen from
   * inside, which the renderer also uses for fog.
   */
  update(eye: THREE.Vector3, seaY: number, tSec: number, sky: THREE.Color, under: boolean, underCol: THREE.Color,
         lights?: WaterLights) {
    // wave phase of the beach waves: the integral of the peak frequency, so that a change of the sea state never jumps the crests
    const dt = this.lastT < 0 ? 0 : Math.min(0.25, Math.max(0, tSec - this.lastT));
    this.lastT = tSec;
    this.pumpStats(tSec);
    this.phi = (this.phi + this.u.uOmega.value * dt) % (2 * Math.PI * 4096);
    this.u.uPhi.value = this.phi;
    // the plane lies at the top of the slab the surface lives in, but never above the eye (it would then be seen from below)
    let planeY = seaY;
    if (this.u.uMarch.value > 0.5 && eye.y > seaY + 0.05) planeY = Math.min(seaY + this.u.uHtop.value, eye.y - 0.03);
    this.mesh.position.set(Math.round(eye.x / SNAP_M) * SNAP_M, planeY, Math.round(eye.z / SNAP_M) * SNAP_M);
    const sc = Math.max(1, 2 * this.reachM / PLANE_M);       // the plane is PLANE_M on a side; it grows to cover the eye's reach (never smaller than it was)
    if (this.mesh.scale.x !== sc) this.mesh.scale.set(sc, 1, sc);
    // Everything the shader counts from O (origin.ts): the mesh's offset from it, and O modulo each tile's side and in the noises' cells, all worked out in doubles here.
    this.u.uMeshRel.value.set(this.mesh.position.x - origin.org.x, this.mesh.position.y, this.mesh.position.z - origin.org.y);
    origin.modulo(this.u.uTileL.value.x, this.u.uTO0.value); origin.modulo(this.u.uTileL.value.y, this.u.uTO1.value); origin.modulo(this.u.uTileL.value.z, this.u.uTO2.value);
    origin.cells(5, this.u.uNC5.value); origin.cells(6, this.u.uNC6.value); origin.cells(17, this.u.uNC17.value); origin.cells(1 / RAIN_CELL_M, this.u.uNCR.value);
    if (this.rainVer !== origin.version) this.applyRain();
    this.u.uSeaY.value = seaY;
    const fog = this.scene.fog as THREE.FogExp2 | null;
    this.u.uFogDensity.value = fog && (fog as any).isFogExp2 ? fog.density : 0;
    this.u.uWT.value = tSec;
    this.u.uSky.value.set(sky.r, sky.g, sky.b);
    this.u.uUnderCol.value.set(underCol.r, underCol.g, underCol.b);
    this.u.uUnder.value = under ? 1 : 0;
    if (this.windU) {
      this.u.uWindT.value = this.windU.uWindT.value; this.u.uWindAmp.value = this.windU.uWindAmp.value;
      this.u.uWindDir.value.copy(this.windU.uWindDir.value);
    }
    // glitter lights; none given (or a light below the horizon) means no reflection from it
    const L = lights;
    if (L && L.sunDir.y > 0) { this.u.uSunDir.value.copy(L.sunDir); this.u.uSunK.value.set(L.sunK[0], L.sunK[1], L.sunK[2]); }
    else this.u.uSunK.value.set(0, 0, 0);
    if (L && L.moonDir && L.moonK && L.moonDir.y > 0) {
      this.u.uMoonDir.value.copy(L.moonDir);
      this.u.uMoonK.value.set(L.moonK[0] * MOON_GLITTER_BOOST, L.moonK[1] * MOON_GLITTER_BOOST, L.moonK[2] * MOON_GLITTER_BOOST);
    } else this.u.uMoonK.value.set(0, 0, 0);
  }

  dispose() {
    this.scene.remove(this.mesh);
    this.geo.dispose(); this.material.dispose();
    this.shore?.tex.dispose();
    this.ocean?.dispose(); this.ocean = null;
  }
}
